import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Code, Star, ExternalLink, AlertTriangle } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface PoC {
  cve_id: string
  kind: string
  name: string
  url: string
  stars: number
  language: string
  description: string
  first_seen: string | null
  severity?: string
  cvss_score?: number | null
  kev?: boolean
}

/** Recent public proof-of-concept exploits. Reads from /api/cyber/poc-feed
 * which is a pure DB lookup — no per-request fan-out. The hourly
 * scan_exploits Celery task keeps the table warm. */
export default function PoCFeed({ days = 14, limit = 12 }: { days?: number; limit?: number }) {
  const [pocs, setPocs] = useState<PoC[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    api.get('/api/cyber/poc-feed', { params: { days, limit } })
      .then(r => { if (mounted) setPocs(r.data?.pocs || []) })
      .catch(() => { if (mounted) setPocs([]) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [days, limit])

  if (loading || pocs.length === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Code size={14} className="text-amber" /> Public exploits — last {days}d
          <span className="text-[10px] text-text-muted font-normal">
            {pocs.length} PoC{pocs.length === 1 ? '' : 's'} discovered
          </span>
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
        {pocs.map(p => {
          const sev = (p.severity || '').toLowerCase()
          const sevTone = sev === 'critical' ? 'bg-rose/10 text-rose border-rose/30'
                       : sev === 'high'     ? 'bg-orange/10 text-orange border-orange/30'
                       : sev === 'medium'   ? 'bg-amber/10 text-amber-light border-amber/30'
                       : 'bg-surface-raised text-text-muted border-border'
          return (
            <div key={`${p.cve_id}-${p.url}`}
              className="p-2.5 rounded border border-border hover:bg-surface-hover text-xs space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Link to={`/app/cve/${encodeURIComponent(p.cve_id)}`}
                  className="font-mono text-[11px] text-blue-light hover:underline">
                  {p.cve_id}
                </Link>
                <div className="flex items-center gap-1">
                  {p.kev && <span className="badge-red text-[9px]">KEV</span>}
                  {p.cvss_score && (
                    <span className={clsx('badge text-[9px]', sevTone)}>
                      {p.cvss_score.toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
              <a href={p.url} target="_blank" rel="noreferrer noopener"
                className="block text-[11px] text-text-primary hover:text-blue-light truncate"
                title={p.name}>
                {p.kind === 'github-repo' ? '📦' : '🎯'} {p.name}
                <ExternalLink size={9} className="inline ml-1 opacity-50" />
              </a>
              {p.description && (
                <p className="text-[10px] text-text-secondary line-clamp-2">{p.description}</p>
              )}
              <div className="flex items-center justify-between text-[9px] text-text-muted">
                <span className="flex items-center gap-2">
                  {p.stars > 0 && <span className="flex items-center gap-0.5"><Star size={9} /> {p.stars}</span>}
                  {p.language && <span>{p.language}</span>}
                </span>
                {p.first_seen && (
                  <span title={p.first_seen}>
                    {formatDistanceToNow(new Date(p.first_seen), { addSuffix: true })}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
